import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Snackbar,
  Tooltip, // --- CHANGE 1: Imported Tooltip for the truncated Project ID ---
} from '@mui/material';
import { gql, useLazyQuery } from '@apollo/client';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// --- ASSET PATHS (Ensure these are correct in your project) ---
const YOUR_TEXTURE_SVG_PATH = '/textures/Texture.svg';
const DATA_SOURCE_HEADER_ICON = '/icons/data-sources-blue.svg';
const YOUR_GRADIENT_SVG_PATH = '/images/Gradient_Popup.svg';
const PROVIDER_DETAILS_ICON_SVG = '/icons/left-of-provider-icon.svg';
const BILLING_DETAILS_ICON_SVG = '/icons/billing-details.svg';
const API_KEY_ICON_SVG = '/icons/billing-details.svg';

const getProviderIcon = (providerType: string): string => {
    switch (providerType) {
      case 'GCP': return '/icons/google-cloud-icon-2048x1646-7admxejz.svg';
      case 'LANGFUSE': return '/icons/llm-observability-blue.svg';
      case 'AWS': return '/icons/aws.png';
      case 'AZURE': return '/icons/azure.svg';
      default: return '';
    }
};

const getProviderName = (providerType: string): string => {
    switch (providerType) {
        case 'GCP': return 'Google Cloud Platform';
        case 'LANGFUSE': return 'Langfuse';
        default: return providerType;
    }
}

interface CloudDataSource {
    __typename: 'DataSource';
    id: string;
    name: string;
    providerType: 'GCP';
    billingAccountId: string;
    billingConfig: {
        bigQueryProjectId: string;
        bigQueryDatasetId: string;
        bigQueryDataTableName: string;
    } | string | null;
}

interface LlmDataSource {
    __typename: 'LlmDataSource';
    id: string;
    name: string;
    providerType: 'LANGFUSE';
    projectId: string;
    configType: 'MANAGED' | 'SELF_HOSTED' | string;
}

type AnyDataSource = CloudDataSource | LlmDataSource;

interface DetailPanelProps {
    dataSourceId: string | null;
    onClose: () => void;
}

const GET_ANY_DATA_SOURCE_DETAILS = gql`
    query GetAnyDataSourceDetails($dataSourceId: ID!) {
        getAnyDataSourceById(id: $dataSourceId) {
            __typename
            ... on DataSource {
                id
                name
                providerType
                billingAccountId
                billingConfig
            }
            ... on LlmDataSource {
                id
                name
                providerType
                configType
                projectId
            }
        }
    }
`;

const GET_LLM_DATA_SOURCE_KEYS = gql`
    query GetLlmDataSourceKeys($dataSourceId: ID!) {
        getLlmDataSourceKeys(id: $dataSourceId) {
            publicKey
            secretKey
        }
    }
`;

// --- CHANGE 2: Enhanced DetailItem to handle truncation and tooltips ---
const DetailItem = ({ label, value, maxLength }: { label: string; value: string | undefined | null; maxLength?: number }) => {
    const displayValue = value || 'N/A';
    const isTruncated = maxLength && value && value.length > maxLength;

    const textElement = (
        <Typography
            variant="body1"
            sx={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 500,
                color: '#333333',
                lineHeight: 1.5,
                mt: 0.5,
                wordBreak: 'break-word',
            }}
        >
            {isTruncated ? `${value.slice(0, maxLength)}...` : displayValue}
        </Typography>
    );

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="#666666" sx={{ fontFamily: 'Inter', fontSize:'12px', fontWeight: 400 }}>
                {label}
            </Typography>
            {isTruncated ? (
                <Tooltip title={displayValue} arrow>
                    {textElement}
                </Tooltip>
            ) : (
                textElement
            )}
        </Box>
    );
};


const DataSourceDetailPanel: React.FC<DetailPanelProps> = ({ dataSourceId, onClose }) => {
    const navigate = useNavigate();
    const [showKeys, setShowKeys] = useState(false);
    const [copied, setCopied] = useState(false);

    const [getDetails, { data, loading, error }] = useLazyQuery<{ getAnyDataSourceById: AnyDataSource }>(GET_ANY_DATA_SOURCE_DETAILS);
    const [getKeys, { data: keysData, loading: keysLoading, error: keysError }] = useLazyQuery(GET_LLM_DATA_SOURCE_KEYS);

    const details = data?.getAnyDataSourceById;

    useEffect(() => {
        if (dataSourceId) {
            setShowKeys(false);
            getDetails({ variables: { dataSourceId } });
        }
    }, [dataSourceId, getDetails]);

    const handleNavigateToEdit = () => {
        if (dataSourceId) {
            navigate(`/data-sources/edit/${dataSourceId}`);
        }
    };

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
    };

    const renderCloudDetails = (ds: CloudDataSource) => {
        const billingConfig = ds.billingConfig
            ? typeof ds.billingConfig === 'string'
                ? JSON.parse(ds.billingConfig)
                : ds.billingConfig
            : null;

        return (
            <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#F2F7FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={BILLING_DETAILS_ICON_SVG} alt="Billing Details" style={{ width: 14, height: 14 }} />
                    </Box>
                    <Typography variant="body1" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', color: '#333333' }}>
                        Billing Account Details
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    <Box sx={{ flexBasis: 'calc(50% - 8px)', flexGrow: 1 }}>
                        <DetailItem label="Data Source Name" value={ds.name} />
                        <DetailItem label="BigQuery Project ID" value={billingConfig?.bigQueryProjectId} />
                        <DetailItem label="BigQuery Table Name" value={billingConfig?.bigQueryDataTableName} />
                    </Box>
                    <Box sx={{ flexBasis: 'calc(50% - 8px)', flexGrow: 1 }}>
                        <DetailItem label="Billing Account ID" value={ds.billingAccountId} />
                        <DetailItem label="BigQuery Dataset ID" value={billingConfig?.bigQueryDatasetId} />
                    </Box>
                </Box>
            </>
        );
    }
const renderLlmDetails = (ds: LlmDataSource) => {
    return (
        <>
            {/* This part always shows */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#F2F7FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src={API_KEY_ICON_SVG} alt="API Key Details" style={{ width: 14, height: 14 }} />
                </Box>
                <Typography variant="body1" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', color: '#333333' }}>
                    Project Details
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                <Box sx={{ flexBasis: 'calc(50% - 8px)', flexGrow: 1 }}>
                        <DetailItem label="Data Source Name" value={ds.name} />
                </Box>
                    <Box sx={{ flexBasis: 'calc(50% - 8px)', flexGrow: 1, minWidth: 0 }}>
                    <DetailItem label="Project ID" value={ds.projectId} maxLength={20} />
                </Box>
            </Box>
            
            {/* --- NEW: Conditional check for the API Keys section --- */}
            {ds.configType === 'MANAGED' && (
                <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body1" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', color: '#333333', mb: 2 }}>
                        API Keys
                    </Typography>
                    {!keysData && !keysLoading && (
                        <Button variant="outlined" onClick={() => getKeys({ variables: { dataSourceId } })} sx={{ textTransform: 'none', maxWidth: '150px', color: '#0064FF', borderColor: '#0064FF' }}>
                            View API Keys
                        </Button>
                    )}
                    {keysLoading && <CircularProgress size={24} />}
                    {keysError && <Alert severity="error">Could not load API keys.</Alert>}
                    {keysData && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Public Key"
                                value={keysData.getLlmDataSourceKeys.publicKey}
                                InputProps={{
                                    sx: { '& .MuiInputBase-input': { fontSize: '12px' } },
                                    readOnly: true,
                                    endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => handleCopy(keysData.getLlmDataSourceKeys.publicKey)}>
                                            <ContentCopyIcon sx={{ fontSize: '18px' }} />
                                        </IconButton>
                                    </InputAdornment>
                                )}}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Secret Key"
                                type={showKeys ? 'text' : 'password'}
                                value={keysData.getLlmDataSourceKeys.secretKey}
                                InputProps={{
                                    sx: { '& .MuiInputBase-input': { fontSize: '12px' } },
                                    readOnly: true,
                                    endAdornment: (
                                    <>
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowKeys(!showKeys)}>
                                                {showKeys ? <VisibilityOffIcon sx={{ fontSize: '18px' }} /> : <VisibilityIcon sx={{ fontSize: '18px' }} />}
                                            </IconButton>
                                        </InputAdornment>
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => handleCopy(keysData.getLlmDataSourceKeys.secretKey)}>
                                                <ContentCopyIcon sx={{ fontSize: '18px' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    </>
                                )}}
                                fullWidth
                                size="small"
                            />
                        </Box>
                    )}
                </>
            )}
        </>
    );
}

    return (
        <Box sx={{
            width : '100%', // Set to 100% to be flexible within the Drawer
            height: '100%',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
            borderRadius: '12px'
        }}>
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0,
                backgroundImage: `url(${YOUR_TEXTURE_SVG_PATH}), url(${YOUR_GRADIENT_SVG_PATH})`,
                backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center',
                borderRadius: '8px', p: 2, minHeight: '120px',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '10px', backgroundColor: '#E5EFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={DATA_SOURCE_HEADER_ICON} alt="Data Source" style={{ width: 20, height: 20 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '18px', color: '#333333' }}>
                        Data Source Details
                    </Typography>
                </Box>
                {/* --- ALIGNMENT FIX 1: Removed margin-right (mr) and adjusted padding on parent --- */}
                <IconButton onClick={onClose} sx={{ color: '#333333' }}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 3 }}>
                {loading && <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}><CircularProgress /></Box>}
                {error && <Alert severity="error">Could not load details: {error.message}</Alert>}
                {details && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#F2F7FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <img src={PROVIDER_DETAILS_ICON_SVG} alt="Provider Details Icon" style={{ width: 14, height: 14 }} />
                            </Box>
                            <Typography variant="body1" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', color: '#333333' }}>
                                Provider Details
                            </Typography>
                        </Box>
                        <DetailItem label="Provider" value={getProviderName(details.providerType)} />
                        <Divider sx={{ mt:0.5, mb:0.5, mx: -3 }} />
                        {details.__typename === 'DataSource' && renderCloudDetails(details)}
                        {details.__typename === 'LlmDataSource' && renderLlmDetails(details)}
                    </Box>
                )}
            </Box>

            <Divider sx={{ mb: 2 }}/>
            <Box sx={{ p: 3, pt: 1, mt: 'auto', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    // --- ALIGNMENT FIX 2: Removed margin-right (mr) ---
                    sx={{ textTransform: 'none', fontWeight: 500, fontSize:'14px', borderRadius: '8px', px: 2, fontFamily:'Inter', py: 1, backgroundColor: '#0064FF' }}
                    onClick={handleNavigateToEdit}
                    disabled={!dataSourceId}
                >
                    Edit Data Source
                </Button>
            </Box>
            <Snackbar
                open={copied}
                autoHideDuration={2000}
                onClose={() => setCopied(false)}
                message="Copied to clipboard!"
            />
        </Box>
    );
};

export default DataSourceDetailPanel;





// import React, { useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   Button,
//   Divider,
//   IconButton,
//   CircularProgress,
//   Alert,
// } from '@mui/material';
// import { gql, useLazyQuery } from '@apollo/client';
// import { Close as CloseIcon } from '@mui/icons-material';
// import { useNavigate } from 'react-router-dom';

// // --- PLACEHOLDERS FOR YOUR SVG ASSETS ---
// const YOUR_TEXTURE_SVG_PATH = '/textures/Texture.svg'; // <--- **REPLACE THIS PATH**
// const DATA_SOURCE_HEADER_ICON = '/icons/data-sources-blue.svg'; // <--- **REPLACE THIS PATH**
// const YOUR_GRADIENT_SVG_PATH = '/images/Gradient_Popup.svg'; // <--- **ADD THIS LINE AND REPLACE WITH YOUR ACTUAL PATH**
// const PROVIDER_DETAILS_ICON_SVG = '/icons/left-of-provider-icon.svg'; // <--- **ADD THIS LINE AND REPLACE WITH YOUR ACTUAL ICON SVG PATH**
// const BILLING_DETAILS_ICON_SVG = '/icons/billing-details.svg';
// // Helper to get provider icons
// const getProviderIcon = (providerType: string): string => {
//     switch (providerType) {
//       case 'GCP': return '/icons/google-cloud-icon-2048x1646-7admxejz.svg'; 
//       case 'AWS': return '/icons/aws.png';
//       case 'AZURE': return '/icons/azure.svg';
//       default: return '';
//     }
// };

// // Define the detailed type needed for this panel
// interface DataSourceDetails {
//     id: string;
//     name: string;
//     providerType: 'GCP' | 'AWS' | 'AZURE' | string;
//     billingAccountId: string;
//     // The billingConfig can be a string, so we need to parse it if it is.
//     billingConfig: {
//         bigQueryProjectId: string;
//         bigQueryDatasetId:string;
//         bigQueryDataTableName: string;
//     } | string | null;
// }

// interface DetailPanelProps {
//     dataSourceId: string | null;
//     onClose: () => void;
// }

// // FIX 1: Define the DetailItem interface
// interface DetailItem {
//     label: string;
//     value: string | undefined | null;
// }

// // Updated GraphQL query to fetch billingConfig
// const GET_DATA_SOURCE_DETAILS = gql`
//     query GetDataSourceDetails($dataSourceId: ID!) {
//         dataSourceById(id: $dataSourceId) {
//             id
//             name
//             providerType
//             billingAccountId
//             billingConfig
//         }
//     }
// `;

// const DataSourceDetailPanel: React.FC<DetailPanelProps> = ({ dataSourceId, onClose }) => {
//     const navigate = useNavigate();

//     const handleNavigateToEdit = () => {
//         if (dataSourceId) {
//             navigate(`/data-sources/edit/${dataSourceId}`);
//         } else {
//             console.error("Cannot navigate to edit page: No Data Source ID provided.");
//         }
//     };

//     const [
//         getDetails, 
//         { data: detailsData, loading: detailsLoading, error: detailsError }
//     ] = useLazyQuery<{ dataSourceById: DataSourceDetails }>(GET_DATA_SOURCE_DETAILS);

//     useEffect(() => {
//         if (dataSourceId) {
//             getDetails({ variables: { dataSourceId } });
//         }
//     }, [dataSourceId, getDetails]);

//     // Parse billingConfig if it's a string
//     const details = detailsData?.dataSourceById;
//     const billingConfig = details?.billingConfig 
//         ? typeof details.billingConfig === 'string' 
//             ? JSON.parse(details.billingConfig) 
//             : details.billingConfig 
//         : null;

//     // FIX 2: Define the data arrays for the columns based on the desired layout
//     const leftColumnData: DetailItem[] = details ? [
//         { label: 'Data Source Name', value: details.name },
//         { label: 'BigQuery Project ID', value: billingConfig?.bigQueryProjectId || 'N/A' },
//         { label: 'BigQuery Table Name', value: billingConfig?.bigQueryDataTableName || 'N/A' },
//     ] : [];

//     const rightColumnData: DetailItem[] = details ? [
//         { label: 'Billing Account ID', value: details.billingAccountId },
//         { label: 'BigQuery Dataset ID', value: billingConfig?.bigQueryDatasetId || 'N/A' },
//     ] : [];

//     return (
//         <Box sx={{ 
//             width : '500px',
//             height: '920px',
//             p: 0, 
//             display: 'flex', 
//             flexDirection: 'column', 
//             backgroundColor: 'white',
//             borderRadius: '12px' 
//         }}>
//             {/* Top Header Section */}
//             <Box sx={{ 
//                 display: 'flex', 
//                 justifyContent: 'space-between', 
//                 alignItems: 'center',
//                 mb: 2, 
//                 flexShrink: 0,
//                 backgroundImage: `url(${YOUR_TEXTURE_SVG_PATH}), url(${YOUR_GRADIENT_SVG_PATH})`,  
//                 backgroundRepeat: 'no-repeat',
//                 backgroundSize: 'cover',
//                 backgroundPosition: 'center',
//                 borderRadius: '8px',
//                 p: 1,
//                 minHeight: '120px',
//             }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p:2, marginBottom:2 }}>
//                     <Box sx={{ 
//                         width: 56, 
//                         height: 56, 
//                         borderRadius: '10px', 
//                         backgroundColor: '#E5EFFF',
//                         display: 'flex', 
//                         alignItems: 'center', 
//                         justifyContent: 'center',
//                         flexShrink: 0,
//                     }}>
//                         <img src={DATA_SOURCE_HEADER_ICON} alt="Data Source" style={{ width: 20, height: 20 }} />
//                     </Box>
//                     <Typography variant="h6" sx={{ 
//                         fontFamily: 'Inter',
//                         fontWeight: 600, 
//                         fontSize: '18px',
//                         color: '#333333', 
//                     }}>
//                         Data Source Details
//                     </Typography>
//                 </Box>
//                 <IconButton onClick={onClose} sx={{ color: '#333333', mb:4, mr:8 }}>
//                     <CloseIcon />
//                 </IconButton>
//             </Box>

//             <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 3 }}>
//                 {detailsLoading && <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}><CircularProgress /></Box>}
//                 {detailsError && <Alert severity="error">Could not load details: {detailsError.message}</Alert>}
                
//                 {details && (
//                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
//                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> 
//                             <Box sx={{
//                                 width: 28, 
//                                 height: 28, 
//                                 borderRadius: '50%', 
//                                 backgroundColor: '#F2F7FF', 
//                                 display: 'flex',
//                                 alignItems: 'center',
//                                 justifyContent: 'center',
//                                 flexShrink: 0, 
//                             }}>
//                                 <img src={PROVIDER_DETAILS_ICON_SVG} alt="Provider Details Icon" style={{ width: 14, height: 14 }} /> 
//                             </Box>
                            
//                             <Typography 
//                                 variant="body1" 
//                                 sx={{ 
//                                     fontFamily: 'Inter',
//                                     fontWeight: 600,
//                                     fontSize: '14px',
//                                     lineHeight: '20px',
//                                     color: '#333333',
//                                     px: 0.5, 
//                                 }}
//                             >
//                                 Provider Details
//                             </Typography>
//                         </Box>

//                         <Typography 
//                             sx={{
//                                 fontFamily: 'Inter',
//                                 fontWeight: 400,
//                                 fontSize: '12px',
//                                 lineHeight: '18px',
//                                 color: '#666666',
//                                 px: 0.2,
//                                 py: 0.25,
//                                 mt:1
//                             }}
//                         >
//                             Provider
//                         </Typography>
//                         <Typography 
//                             sx={{
//                                 fontFamily: 'Inter',
//                                 fontWeight: 500,
//                                 fontSize: '14px',
//                                 lineHeight: '17.18px',
//                                 color: '#333333',
//                                 px: 0.2,
//                                 py: 0.2, 
//                                 mt:-2                 
//                             }}
//                         >
//                             Google Cloud Platform
//                         </Typography>

//                         <Divider sx={{ mt:0.5, mb:0.5, mx: -3 }} /> 
//                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> 
//                             <Box sx={{
//                                 width: 28, 
//                                 height: 28, 
//                                 borderRadius: '50%', 
//                                 backgroundColor: '#F2F7FF', 
//                                 display: 'flex',
//                                 alignItems: 'center',
//                                 justifyContent: 'center',
//                                 flexShrink: 0, 
//                             }}>
//                                 <img src={BILLING_DETAILS_ICON_SVG} alt="Provider Details Icon" style={{ width: 14, height: 14 }} /> 
//                             </Box>
                            
//                             <Typography 
//                                 variant="body1" 
//                                 sx={{ 
//                                     fontFamily: 'Inter',
//                                     fontWeight: 600,
//                                     fontSize: '14px',
//                                     lineHeight: '20px',
//                                     color: '#333333',
//                                     px: 0.5, 
//                                 }}
//                             >
//                                 Billing Account Details
//                             </Typography>
//                         </Box>
//                         {/* <Typography variant="body1" sx={{fontSize:'14px', fontWeight: 600, color: 'text.primary' }}>
//                             Billing Account Details
//                         </Typography>                         */}
                        
//                         {/* Flexbox container for Two Columns */}
//                         <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}> 
//                             {/* Left Column Container */}
//                             <Box sx={{ flexBasis: 'calc(50% - 8px)', flexGrow: 1, flexDirection: 'column' }}> 
//                                 {leftColumnData.map((item) => ( 
//                                     <Box key={item.label} sx={{ mb: 3 }}> 
//                                         <Typography 
//                                             variant="body2" 
//                                             color=" #666666" 
//                                             sx={{ 
//                                                 fontFamily: 'Inter',     
//                                                 fontSize:'12px', 
//                                                 fontWeight: 400,         
//                                             }} 
//                                         >
//                                             {item.label}
//                                         </Typography>
//                                         <Typography 
//                                             variant="body1" 
//                                             sx={{ 
//                                                 fontFamily: 'Inter',     
//                                                 fontSize:'14px',         
//                                                 fontWeight: 500,         
//                                                 color: '#333333', 
//                                                 lineHeight: 1.5,
//                                                 mt: 0.5 // <-- Added gap here
//                                             }}
//                                         >
//                                             {item.value}
//                                         </Typography>
//                                     </Box>
//                                 ))}
//                             </Box>
                            
//                             {/* Right Column Container */}
//                             <Box sx={{ flexBasis: 'calc(50% - 8px)', flexGrow: 1, flexDirection: 'column' }}> 
//                                 {rightColumnData.map((item) => ( 
//                                     <Box key={item.label} sx={{ mb: 3 }}>
//                                         <Typography 
//                                             variant="body2" 
//                                             color=" #666666" 
//                                             sx={{ 
//                                                 fontFamily: 'Inter',     
//                                                 fontSize:'12px', 
//                                                 fontWeight: 400,         
//                                             }} 
//                                         >
//                                             {item.label}
//                                         </Typography>
//                                         <Typography 
//                                             variant="body1" 
//                                             sx={{ 
//                                                 fontFamily: 'Inter',     
//                                                 fontSize:'14px',         
//                                                 fontWeight: 500,         
//                                                 color: ' #333333', 
//                                                 lineHeight: 1.5 ,
//                                                 mt: 0.5 // <-- Added gap here

//                                             }}
//                                         >
//                                             {item.value}
//                                         </Typography>
//                                     </Box>
//                                 ))}
//                             </Box>
//                         </Box>
//                     </Box>
//                 )}
//             </Box>

//             <Divider sx={{ mb: 2 }}/>

//             <Box sx={{ p: 3, pt: 1, mt: 'auto', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
//                 <Button 
//                     variant="contained" 
//                     sx={{ 
//                         textTransform: 'none', 
//                         fontWeight: 500, 
//                         fontSize:'14px',
//                         borderRadius: '8px', 
//                         px: 2, 
//                         fontFamily:'Inter',
//                         py: 1,
//                         mr:6,
//                         backgroundColor: '#0064FF',
//                     }} 
//                     onClick={handleNavigateToEdit}
//                     disabled={!dataSourceId}
//                 >
//                     Edit Data Source
//                 </Button>
//             </Box>
//         </Box>
//     );
// };

// export default DataSourceDetailPanel;
